import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ALL_ASSESSMENTS_PAGE_SIZE } from './../all-assessments/all-assessments-table/all-assessment-table.config';
import { computed, inject, signal, Signal } from '@angular/core';
import { Assessment } from '../models/assessment.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { AssessmentsApiService } from '../services/assessments-api.service';
import { AuthService } from '@core/auth/auth.service';
import { AssessmentsBaseFilter, AssessmentsFilter } from '../models/assessment-filter.model';
import { GridApi, IDatasource } from 'ag-grid-community';
import { createGridDataSource } from '@shared/data-sources/grid-datasource.factory';
import { withAllAssessmentFilterDataFeature } from './with-all-assessment-filter-data.feature';
import { finalize, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';

type AllAssessmentState = {
  myScope: boolean;
  baseFilter: AssessmentsBaseFilter;
  _gridApi: GridApi | undefined;
  dataSource: IDatasource | undefined;
  isLoading: Signal<boolean>;
  isLoadingExport: boolean;
};

const initialState: AllAssessmentState = {
  myScope: true,
  _gridApi: undefined,
  dataSource: undefined,
  isLoading: signal(false),
  baseFilter: {},
  isLoadingExport: false,
};

export const AllAssessmentStore = signalStore(
  withState(initialState),
  withAllAssessmentFilterDataFeature(),
  withComputed(store => ({
    filter: computed<AssessmentsFilter>(() => ({ ...store.baseFilter(), ...store.selectedDialogFilter() })),
  })),
  withMethods((store, assessmentApiService = inject(AssessmentsApiService), authService = inject(AuthService)) => {
    const { dataSource, isLoading } = createGridDataSource<AssessmentsFilter, Assessment>({
      fetchData: request => assessmentApiService.getAssessments$(request),
      getFilter: () => store.filter(),
      pageSize: ALL_ASSESSMENTS_PAGE_SIZE,
    });

    patchState(store, { isLoading });

    const initGridApi = (_gridApi: GridApi) => {
      patchState(store, { _gridApi });
      store._gridApi()?.setGridOption('datasource', dataSource);
    };

    const setMyScope = (my: boolean) => {
      patchState(store, { myScope: my });
      const baseFilter = my ? { ...store.baseFilter(), userId: authService.user()?.userId } : { ...store.baseFilter(), userId: undefined };
      if (my) {
        store.resetDialogFilter();
      }
      patchState(store, { baseFilter });
      // reset datasource to apply the new filter
      store._gridApi()?.setGridOption('datasource', dataSource);
    };

    const setKeywordFilter = (searchPhrase: string) => {
      patchState(store, { baseFilter: { ...store.baseFilter(), searchPhrase } });
      // reset datasource to apply the new filter
      store._gridApi()?.setGridOption('datasource', dataSource);
    };

    const submitFilter = () => {
      // reset datasource to apply the new filter
      store._gridApi()?.setGridOption('datasource', dataSource);
    };

    const exportExcel = rxMethod<void>(
      pipe(
        tap(() => {
          patchState(store, { isLoadingExport: true });
        }),
        switchMap(() =>
          assessmentApiService.exportAssessments$(store.filter()).pipe(
            tapResponse({
              next: response => {
                const contentDisposition = response.headers.get('Content-Disposition');
                const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
                if (response.body) {
                  saveFile(response.body, filename);
                }
              },
              error: () => {
                // error snackbar handled in service
              },
            }),
            finalize(() => {
              patchState(store, { isLoadingExport: false });
            })
          )
        )
      )
    );

    return { setMyScope, initGridApi, setKeywordFilter, submitFilter, exportExcel };
  }),
  withHooks({
    onInit(store, authService = inject(AuthService)) {
      if (store.myScope()) {
        patchState(store, { baseFilter: { ...store.baseFilter(), userId: authService.user()?.userId } });
      }
    },
  })
);
